// @flow
import React, { Fragment } from 'react'
import classNames from 'classnames/bind'
import length from 'ramda/es/length'
import { useSelector, shallowEqual } from 'react-redux'

import { Table, Tr, Th, Td, useHover, useHoverByClass } from 'app/common/ui-components/table'

import type { SortHandle, SortState } from 'app/common/containers/withSortState'
import type { TotalType } from 'app/actions/reports/subsidies/data'
import HScrollbarsWShadow from 'app/common/ui-components/h-scrollbars-w-shadow'
import Paging from 'app/common/ui/Paging'
import * as viewTypesAvailable from 'app/actions/reports/filters/viewTypesAvailable'
import { useCurrencySign, options as co } from 'app/common/components-next/currency'
import DetailsHeader from 'app/components/common/reports/DetailsHeader'
import { isValueZero } from 'app/common/templates-next/table-template'

import enhanceClasses from './enhanceClasses'
import type { ClassType } from './selectActual'

import styles from './Classes.scss'

const cx = classNames.bind(styles)
const sameColumnBase = 'column_'

/**
 * Таблица отчета по классам
 */

type Props = {
  handlePage: (e: SyntheticEvent<HTMLElement>) => void,
  classes: Array<ClassType>,
  totals: Array<TotalType>,
  currentPage: number,
  total: number,
  rows: number,
  sort: SortState,
  handleSort: SortHandle,
}

const Classes = ({
  handlePage,
  classes,
  totals,
  currentPage,
  total,
  rows,
  sort: { sortDirection, sortKey },
  handleSort,
}: Props) => {
  const viewType = useSelector((state) => state.reports.subsidies.params.viewType, shallowEqual)
  const [refTableBody, onMouseEnter, onMouseLeave] = useHover({ numberOfChunks: 3 })
  const [onCellMouseEnter, onCellMouseLeave] = useHoverByClass(sameColumnBase)

  const currency = useCurrencySign(co.COMMA_SPACE_BEFORE)

  return (
    <Fragment>
      <DetailsHeader reportTitle="Дотации" />

      {/* ЗАГОЛОВОК */}
      <div className={styles.stickableTableHeader}>
        {/* Видимый заголовок первой таблицы */}
        <Table className={styles.leftFixed}>
          <thead>
            <Tr header>
              <Th
                id="header1"
                className={cx(styles.headerCell, styles.headerCellClassTitle)}
                sortKey="classTitle"
                sortDirection={sortKey === 'classTitle' ? sortDirection : undefined}
                onSortArrowClick={handleSort}
                alignRight
              >
                {'Класс'}
              </Th>
            </Tr>
          </thead>
          <tbody ref={refTableBody}>
            {classes.map(({ idx, classTitle }, i) => (
              <Tr
                key={idx}
                className={`index_${i}`}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                hoverHighlighted={false}
                interlineBorder
              >
                <Td headers="header1" alignRight>
                  {classTitle}
                </Td>
              </Tr>
            ))}
          </tbody>
          <tfoot>
            <Tr hoverHighlighted={false} className={styles.footer}>
              <Th id="hTotal" alignRight>
                {'Итого:'}
              </Th>
            </Tr>
          </tfoot>
        </Table>

        <div className={styles.hScrollableContent}>
          <HScrollbarsWShadow
            hideVerticalScrollBar
            hideHorizontalScrollBar
            absolutelyHideHorizontal_FixFF
          >
            <Table className={styles.middleVariable}>
              <thead>
                <Tr header>
                  {totals.map(({ idx: cellIdx, title }, j) => (
                    <Th
                      key={cellIdx}
                      id={`headerMiddle${j}`}
                      className={cx(styles.headerCell, `${sameColumnBase}${j}`)}
                      onMouseEnter={onCellMouseEnter}
                      onMouseLeave={onCellMouseLeave}
                      alignRight
                    >
                      {title}
                    </Th>
                  ))}
                </Tr>
              </thead>
              <tbody ref={refTableBody}>
                {classes.map(({ idx, data }, i) => (
                  <Tr
                    key={idx}
                    className={`index_${i}`}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    hoverHighlighted={false}
                    interlineBorder
                  >
                    {data.map(({ idx: cellIdx, sum, count }, j) => (
                      <Td
                        key={cellIdx}
                        headers={`headerMiddle${j}`}
                        className={cx(`${sameColumnBase}${j}`, {
                          tinted: isValueZero(sum) || isValueZero(count),
                        })}
                        onMouseEnter={onCellMouseEnter}
                        onMouseLeave={onCellMouseLeave}
                        alignRight
                      >
                        {viewType === viewTypesAvailable.VIEW_TYPE_SUM ? sum : count}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </tbody>
              <tfoot>
                <Tr hoverHighlighted={false} className={styles.footer}>
                  {totals.map(({ idx, sum, count }, j) => (
                    <Td
                      key={idx}
                      headers={`headerMiddle${j} hTotal`}
                      className={cx(styles.hilightedCell, `${sameColumnBase}${j}`, {
                        tinted: isValueZero(sum) || isValueZero(count),
                      })}
                      onMouseEnter={onCellMouseEnter}
                      onMouseLeave={onCellMouseLeave}
                      alignRight
                    >
                      {viewType === viewTypesAvailable.VIEW_TYPE_SUM ? sum : count}
                    </Td>
                  ))}
                </Tr>
              </tfoot>
            </Table>
          </HScrollbarsWShadow>
        </div>

        <Table className={styles.rightFixed}>
          <thead>
            <Tr header>
              <Th
                id="headerLast"
                className={cx(styles.headerCell, styles.headerCellTotal)}
                sortKey="total"
                sortDirection={sortKey === 'total' ? sortDirection : undefined}
                onSortArrowClick={handleSort}
                alignRight
              >
                {`Всего${currency}`}
              </Th>
            </Tr>
          </thead>
          <tbody ref={refTableBody}>
            {classes.map(({ idx, total: ttl }, i) => (
              <Tr
                key={idx}
                className={cx(`index_${i}`, {
                  tinted: isValueZero(ttl),
                })}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                hoverHighlighted={false}
                interlineBorder
              >
                <Td headers="headerLast" alignRight>
                  {ttl}
                </Td>
              </Tr>
            ))}
          </tbody>
          <tfoot>
            <Tr hoverHighlighted={false} className={styles.footer}>
              <Td>&nbsp;</Td>
            </Tr>
          </tfoot>
        </Table>
      </div>

      {/* Постраничная навигация */}
      {total > rows && (
        <Paging
          total={total}
          pageSize={rows}
          currentPage={currentPage}
          currentPageSize={length(classes)}
          onPage={handlePage}
        />
      )}
    </Fragment>
  )
}

export default enhanceClasses(Classes)
